import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    Activity,
    Boxes,
    ClipboardList,
    FileQuestion,
    LayoutDashboard,
    ListChecks,
    LogOut,
    Package,
    Settings,
    ShieldCheck,
    Smartphone,
    TrendingUp,
    UserCog,
    Users,
    Warehouse,
} from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { PhotoQueueBadge } from "../components/photo/PhotoQueueBadge";
import { useAuth } from "../context/AuthContext";
import { usePhotoQueueWorker } from "../hooks/usePhotoQueueWorker";

const SIDEBAR_ITEMS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "side-dashboard" },
    { to: "/admin/artikel", label: "Artikel", icon: Package, testid: "side-artikel" },
    { to: "/admin/ausleihen", label: "Ausleihen", icon: UserCog, testid: "side-ausleihen" },
    { to: "/admin/lagerstruktur", label: "Lagerstruktur", icon: Warehouse, testid: "side-lager" },
    { to: "/admin/benutzer", label: "Benutzer", icon: Users, testid: "side-benutzer" },
    { to: "/admin/nachbestellung", label: "Nachbestellung", icon: Boxes, testid: "side-nachbest" },
    { to: "/admin/inventur", label: "Inventur", icon: ListChecks, testid: "side-inventur" },
    { to: "/admin/picklisten", label: "Picklisten", icon: ClipboardList, testid: "side-picklisten" },
    { to: "/admin/audit", label: "Audit", icon: ShieldCheck, testid: "side-audit" },
    { to: "/admin/datenqualitaet", label: "Datenqualität", icon: TrendingUp, testid: "side-dq" },
    { to: "/admin/clarifications", label: "Offene Gegenstände", icon: FileQuestion, testid: "side-clar" },
    { to: "/admin/einstellungen", label: "Einstellungen", icon: Settings, testid: "side-settings" },
];

export default function DesktopShell() {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    usePhotoQueueWorker();

    return (
        <div className="pw-grain flex min-h-screen w-full bg-background text-foreground">
            <aside className="sticky top-0 flex h-screen w-64 flex-col border-r border-border bg-card/70">
                <div className="flex items-center gap-3 px-5 py-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <span className="text-base font-black tracking-tighter">P</span>
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-semibold text-foreground">Platzwart</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            Admin Console
                        </div>
                    </div>
                </div>

                <nav className="flex-1 overflow-y-auto px-2 pb-4" data-testid="desktop-sidebar">
                    <ul className="flex flex-col gap-0.5">
                        {SIDEBAR_ITEMS.map((item) => {
                            const Icon = item.icon;
                            return (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        data-testid={item.testid}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                                                isActive
                                                    ? "bg-primary/15 text-foreground shadow-[inset_2px_0_0_rgb(var(--primary))]"
                                                    : "text-muted-foreground hover:bg-card hover:text-foreground"
                                            }`
                                        }
                                    >
                                        <Icon className="h-4 w-4" />
                                        <span>{item.label}</span>
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="mt-3 border-t border-border pt-3">
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            data-testid="desktop-floor-mode"
                            className="pw-tap flex w-full items-center gap-3 rounded-md border border-primary/40 px-3 py-2 text-sm text-primary hover:bg-primary/10"
                        >
                            <Smartphone className="h-4 w-4" />
                            <span className="flex-1 text-left">Floor Mode</span>
                        </button>
                    </div>
                </nav>

                <div className="border-t border-border px-4 py-3">
                    <div className="mb-3 text-xs leading-tight text-muted-foreground">
                        <div className="flex items-center gap-1 text-foreground">
                            <Activity className="h-3 w-3 text-primary" />
                            <span className="font-medium">{user?.name || user?.email}</span>
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em]">
                            {user?.role}
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <ThemeToggle />
                        <PhotoQueueBadge />
                        <button
                            type="button"
                            onClick={async () => {
                                await signOut();
                                navigate("/login", { replace: true });
                            }}
                            className="pw-tap flex items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
                            data-testid="desktop-logout-btn"
                        >
                            <LogOut className="h-4 w-4" />
                            <span>Abmelden</span>
                        </button>
                    </div>
                </div>
            </aside>

            <main className="min-h-screen flex-1 overflow-y-auto px-10 py-10">
                <Outlet />
            </main>
        </div>
    );
}
